// Counterparties matching what has been typed so far, fetched as it is typed.
//
// The same debounce as the register's counterparty filter (TX-US-0006 task 5), for the same
// reason: a request per keystroke is a request per keystroke. What differs is what is guarded.
// The filter writes to the URL, so it had to worry about history; this only shows a list, so it
// has to worry about the list being the wrong one.
//
// TWO THINGS THAT GO WRONG WITHOUT CARE
//
// Responses can land out of order. Type "vit" then "vitol", and if the first request is slower the
// suggestions end up showing matches for "vit" under the text "vitol" - stale, plausible, and
// wrong in the direction that matters, because the BD is deciding whether their counterparty
// already exists. Each request carries a sequence number and only the newest is allowed to land.
//
// And nothing found must render nothing at all. An empty list that lingers reads as "checked, and
// clear", which is the opposite of what it means while a request is still in flight.

import { useEffect, useRef, useState } from 'react';

import { searchCounterparties } from '../../api.js';

// Matches the register filter. Long enough that a burst of typing is one request, short enough
// that the answer arrives while the BD is still looking at the field.
const DELAY_MS = 400;

// The server refuses anything shorter, because one character matches most of the register. Checked
// here too so that the request is not made at all.
const MINIMUM_LENGTH = 2;

export default function useCounterpartySearch(fragment, category) {
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const latest = useRef(0);

  useEffect(() => {
    const query = (fragment ?? '').trim();
    if (query.length < MINIMUM_LENGTH) {
      setMatches([]);
      setSearching(false);
      return undefined;
    }

    setSearching(true);
    const sequence = ++latest.current;
    const timer = setTimeout(async () => {
      try {
        const found = await searchCounterparties(query, category);
        // Only the newest request may render. An older one landing late would replace the right
        // answer with a stale one.
        if (sequence === latest.current) setMatches(found);
      } catch {
        // A failed lookup is not something to put in front of a BD mid-word. They lose the
        // suggestions, not the ability to type a name - and the duplicate gate and the unique
        // index are both still behind this.
        if (sequence === latest.current) setMatches([]);
      } finally {
        if (sequence === latest.current) setSearching(false);
      }
    }, DELAY_MS);

    // Supersede rather than accumulate: a pending request for a fragment the BD has already typed
    // past is an answer nobody wants.
    return () => clearTimeout(timer);
  }, [fragment, category]);

  return { matches, searching };
}
